import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { resources } from '../../core/services/resources';
import { useReportsFilter } from '../../core/contexts/ReportsFilterContext';

/**
 * Filter selectors for the Reports Monitoring module, rendered in the
 * sidebar. Customer → line of business → report template, each optional.
 * With no customer selected the views run cross-tenant (`X-Tenant-Id: *`),
 * so LOB / template selectors — which only make sense inside one customer
 * — stay disabled until a customer is picked.
 */
export function ReportsFilterPanel() {
  const {
    tenantId,
    lineOfBizId,
    reportTemplateId,
    setTenantId,
    setLineOfBizId,
    setReportTemplateId,
  } = useReportsFilter();

  const tenants = useQuery({
    queryKey: ['tenants', 'select'],
    queryFn: () => resources.tenants.list({ pageSize: 500, sort: 'name' }),
  });

  const linesOfBusiness = useQuery({
    queryKey: ['lines-of-business', 'select', tenantId],
    queryFn: () =>
      resources.linesOfBusiness.list({ pageSize: 500, sort: 'name' }, { tenantId: tenantId! }),
    enabled: tenantId != null,
  });

  const templates = useQuery({
    queryKey: ['report-templates', 'select', tenantId, lineOfBizId],
    queryFn: () =>
      resources.reportTemplates.list(
        {
          pageSize: 500,
          sort: 'name',
          filter: lineOfBizId != null ? { lineOfBizId } : undefined,
        },
        { tenantId: tenantId! },
      ),
    enabled: tenantId != null,
  });

  // "All" is encoded as '' in the Select and null in the context.
  const toValue = (id: number | null) => (id == null ? '' : String(id));
  const fromValue = (v: string) => (v === '' ? null : Number(v));

  const selectSx = { mb: 1.5 };

  return (
    <Box sx={{ px: 1.5, py: 1 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
        Filters
      </Typography>
      <TextField
        select
        fullWidth
        size="small"
        label="Customer"
        value={toValue(tenantId)}
        onChange={(e) => setTenantId(fromValue(e.target.value))}
        sx={selectSx}
      >
        <MenuItem value="">All customers</MenuItem>
        {(tenants.data?.data ?? []).map((t) => (
          <MenuItem key={t.id} value={String(t.id)}>
            {t.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        fullWidth
        size="small"
        label="Line of business"
        value={toValue(lineOfBizId)}
        onChange={(e) => setLineOfBizId(fromValue(e.target.value))}
        disabled={tenantId == null}
        sx={selectSx}
      >
        <MenuItem value="">All lines of business</MenuItem>
        {(linesOfBusiness.data?.data ?? []).map((l) => (
          <MenuItem key={l.id} value={String(l.id)}>
            {l.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        fullWidth
        size="small"
        label="Report template"
        value={toValue(reportTemplateId)}
        onChange={(e) => setReportTemplateId(fromValue(e.target.value))}
        disabled={tenantId == null}
      >
        <MenuItem value="">All report templates</MenuItem>
        {(templates.data?.data ?? []).map((t) => (
          <MenuItem key={t.id} value={String(t.id)}>
            {t.name}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}
